import type {SourceOverlayOptions} from './types.js';
import type {
  FeatureSettings,
  SettingSource,
  SettingSources,
} from '../types/timeline.js';
import {BUILTIN_EDITORS} from './runtime/source-overlay/editors.js';
import {SETTINGS, type SourcedKey} from './setting-definitions.js';
import {type CssSheetBuild, resolveCssSheetBuild} from './css-sheet-build.js';
import {ENV_PREFIX, envBool, envEnum, envNum} from './env.js';
import type {ChildStateMode} from './runtime/child-state.js';

export type {CssSheetBuild} from './css-sheet-build.js';

/**
 * On-page HMR feedback: a small pulsing indicator in the corner of the host
 * page that briefly animates on each HMR update, for at-a-glance feedback
 * without watching the console.
 */
export interface HmrIndicatorOptions {
  /** Inject the indicator element. Defaults to `true` (when HMR is enabled). */
  enabled?: boolean;

  /**
   * Show a cumulative update count next to the dot (idle opacity 0.5 instead
   * of fully transparent). Defaults to `false`.
   */
  count?: boolean;
}

/**
 * HMR for Lit component classes and its on-page feedback.
 */
export interface HmrOptions {
  /**
   * Enable in-place HMR for Lit component classes. When `false`, the plugin
   * skips all HMR transforms and runtime injection. Defaults to `true`.
   */
  enabled?: boolean;

  /**
   * Cycle `disconnectedCallback()`/`connectedCallback()` on live instances
   * after a hot patch. Interning makes this mostly unnecessary, so it's
   * opt-in. Defaults to `false`.
   */
  reconnect?: boolean;

  /**
   * Rewrite native `#private` class members to stable `Symbol.for` keys in
   * dev so classes using them can be hot-patched in place. This trades real
   * privacy and brand checks for working HMR, in dev only; builds are never
   * touched. Defaults to `true`.
   */
  privateFields?: boolean;

  /**
   * What to do when a component can't be hot-patched in place (e.g.
   * standard `accessor` decorators). Defaults to `'reload'`.
   */
  onIncompatible?: 'reload' | 'warn';

  /**
   * What happens to custom elements inside a template whose text a hot patch
   * changed. lit-html clones fresh DOM for the edited template, so those
   * elements are re-created.
   *
   * - `'reset'`: they start over from their defaults.
   * - `'transfer'`: each new element takes the old one's reactive properties
   *   and `#private` state (keys the new template binds or sets as an
   *   attribute keep the template's value).
   * - `'reuse'`: where the new element carries no bindings, the old element
   *   (identity, shadow DOM, controllers) is put back in its place; elsewhere
   *   as `'transfer'`.
   *
   * Defaults to `'transfer'`.
   */
  childState?: ChildStateMode;

  /**
   * The on-page update indicator. `true` enables it without a count; an
   * object configures it. Forced off when HMR itself is disabled. Defaults
   * to enabled (without a count).
   */
  indicator?: boolean | HmrIndicatorOptions;
}

/**
 * Options for the Lit Vite plugin.
 *
 * Every option also resolves from environment variables (and `.env` files)
 * with the `LIT_PLUGIN` prefix — e.g. `LIT_PLUGIN_HMR_INDICATOR=true`. Options
 * passed here take precedence over env vars, which take precedence over the
 * built-in defaults.
 */
export interface LitPluginOptions {
  /**
   * HMR for Lit components and its on-page feedback. `true`/`false` toggles
   * the whole feature (patching and indicator); an object configures it.
   * Defaults to enabled.
   */
  hmr?: boolean | HmrOptions;

  /**
   * Dev-only click-to-open-in-IDE inspector for Lit custom elements.
   * Toggle with Ctrl+Shift+S (configurable via `key`). Defaults to `false`.
   */
  sourceOverlay?: boolean | SourceOverlayOptions;

  /**
   * Vite DevTools Timeline panel — a Vue DevTools–style layered event stream
   * for Lit lifecycle, render, mouse, and keyboard events.
   *
   * With the `@vitejs/devtools` Vite plugin (`DevTools()`) active, the panel
   * is a dock in Vite DevTools. Without it, the plugin serves the panel on
   * the dev server at `/__lit/` and prints that URL; its RPC asks for the
   * one-time code printed in the terminal unless
   * `VITE_DEVTOOLS_DISABLE_CLIENT_AUTH=true`. Defaults to `false` while
   * experimental.
   *
   * `true` enables all built-in layers with defaults.
   */
  timeline?: boolean;

  /**
   * What a `?css-sheet` import compiles to under `vite build`.
   * - `'url'`:        fetch-backed sheet over the emitted `.css` asset
   * - `'inline'`:     css text embedded in the JS chunk, processed by the css
   *                   pipeline (`?inline`)
   * - `'inline-raw'`: css text embedded verbatim, skipping the css pipeline
   *                   (`?raw`)
   * - `'auto'`:       `'inline'` when `build.lib` is set, `'url'` otherwise
   *
   * Dev is always the fetch-backed HMR form regardless of this setting.
   * Defaults to `'auto'`.
   */
  cssSheetBuild?: CssSheetBuild;

  /**
   * Answer Chrome's `/.well-known/appspecific/com.chrome.devtools.json` in
   * dev, so DevTools can connect the project as a workspace and save Sources
   * panel edits to disk. `true` uses Vite's workspace root; a string names
   * the folder (relative to Vite's root). Only loopback clients get an
   * answer. Defaults to `true`.
   */
  devtoolsWorkspace?: boolean | string;
}

/** Plugin options after merging explicit options, env vars, and defaults. */
export interface ResolvedOptions {
  hmrEnabled: boolean;
  reconnect: boolean;
  privateFields: boolean;
  onIncompatible: 'reload' | 'warn';
  childState: ChildStateMode;
  indicator: false | {count: boolean};
  sourceOverlay: false | SourceOverlayOptions;
  timeline: boolean;
  cssSheetBuild: CssSheetBuild;
  devtoolsWorkspace: boolean | string;
  /**
   * Which layer each panel-visible setting came from. Source-overlay keys are
   * present only when the overlay is enabled.
   */
  sources: SettingSources;
}

/** Keys a `LIT_PLUGIN_SOURCE_OVERLAY_EDITOR` value may name. */
const EDITOR_KEYS: readonly string[] = Object.keys(BUILTIN_EDITORS);

/** Which layer supplied a value: the first of option, env that is defined. */
const sourceOf = (option: unknown, env: unknown): SettingSource =>
  option !== undefined ? 'option' : env !== undefined ? 'env' : 'default';

/**
 * Take the first defined of an explicit option and an env value, else the
 * default, and record which layer won.
 */
const pick = <T>(
  option: T | undefined,
  env: T | undefined,
  fallback: T
): {value: T; source: SettingSource} => ({
  value: option ?? env ?? fallback,
  source: sourceOf(option, env),
});

type EnvOf = (key: SourcedKey) => string | undefined;

/** The `hmr` group: master toggle and the settings nested under `hmr: {...}`. */
const resolveHmr = (
  options: LitPluginOptions,
  env: Record<string, string>,
  envOf: EnvOf
) => {
  const hmr = options.hmr;
  const hmrObj = typeof hmr === 'object' ? hmr : undefined;
  const enabled = pick(
    typeof hmr === 'boolean' ? hmr : hmrObj?.enabled,
    envBool(envOf('hmr')),
    SETTINGS.hmr.default
  );
  const reconnect = pick(
    hmrObj?.reconnect,
    envBool(envOf('hmrReconnect')),
    SETTINGS.hmrReconnect.default
  );
  const privateFields = pick(
    hmrObj?.privateFields,
    envBool(env[`${ENV_PREFIX}_HMR_PRIVATE_FIELDS`]),
    true
  );
  const onIncompatible = pick<'reload' | 'warn'>(
    hmrObj?.onIncompatible,
    envEnum(
      envOf('hmrOnIncompatible'),
      SETTINGS.hmrOnIncompatible.values!,
      SETTINGS.hmrOnIncompatible.env!
    ),
    SETTINGS.hmrOnIncompatible.default
  );
  const childState = pick<ChildStateMode>(
    hmrObj?.childState,
    envEnum(
      envOf('hmrChildState'),
      SETTINGS.hmrChildState.values!,
      SETTINGS.hmrChildState.env!
    ),
    SETTINGS.hmrChildState.default
  );
  return {
    hmrObj,
    enabled,
    reconnect,
    privateFields,
    onIncompatible,
    childState,
  };
};

/** The HMR indicator: `false`, or `{count}`; it follows the master toggle. */
const resolveIndicator = (
  hmrObj: ReturnType<typeof resolveHmr>['hmrObj'],
  hmrEnabled: boolean,
  envOf: EnvOf
) => {
  const ind = hmrObj?.indicator;
  const indObj = typeof ind === 'object' ? ind : undefined;
  const enabled = pick(
    typeof ind === 'boolean' ? ind : indObj?.enabled,
    envBool(envOf('hmrIndicatorVisible')),
    SETTINGS.hmrIndicatorVisible.default
  );
  const count = pick(
    indObj?.count,
    envBool(envOf('hmrIndicatorCount')),
    SETTINGS.hmrIndicatorCount.default
  );
  // The indicator is meaningless without HMR, so it follows the master toggle.
  const value: false | {count: boolean} =
    hmrEnabled && enabled.value ? {count: count.value} : false;
  return {value, enabled, count};
};

/**
 * Fill the overlay's key/editor/throttle from env where the option leaves them
 * unset, recording which layer supplied each.
 */
const fillOverlayFromEnv = (
  so: LitPluginOptions['sourceOverlay'],
  envOf: EnvOf,
  sources: SettingSources
): SourceOverlayOptions => {
  const base: SourceOverlayOptions = typeof so === 'object' ? {...so} : {};
  // Defaults for these are applied later (toFeatureSettings / the runtime),
  // so only option-vs-env is decided here; neither set means 'default'.
  const envKey = envOf('sourceOverlayKey') || undefined;
  const envThrottle = envNum(envOf('sourceOverlayThrottleMs'));
  // Only read (and warn about) the env editor when no option supplies one.
  const envEditor =
    base.editor === undefined
      ? envEnum(
          envOf('sourceOverlayEditor'),
          EDITOR_KEYS,
          SETTINGS.sourceOverlayEditor.env!
        )
      : undefined;
  sources.sourceOverlayKey = sourceOf(base.key, envKey);
  sources.sourceOverlayEditor = sourceOf(base.editor, envEditor);
  sources.sourceOverlayThrottleMs = sourceOf(base.throttleMs, envThrottle);
  base.key ??= envKey;
  base.editor ??= envEditor;
  base.throttleMs ??= envThrottle;
  return base;
};

/**
 * The source overlay: `false`, or its options with env values filled in.
 * Records the per-key sources (key/editor/throttle only when enabled).
 */
const resolveSourceOverlay = (
  so: LitPluginOptions['sourceOverlay'],
  envOf: EnvOf,
  sources: SettingSources
): false | SourceOverlayOptions => {
  const soExplicit =
    typeof so === 'boolean' ? so : so === undefined ? undefined : true;
  const soEnabled = pick(
    soExplicit,
    envBool(envOf('sourceOverlay')),
    SETTINGS.sourceOverlay.default
  );
  sources.sourceOverlay = soEnabled.source;
  return soEnabled.value ? fillOverlayFromEnv(so, envOf, sources) : false;
};

/**
 * Merge explicit options over env vars over defaults (in that precedence) into
 * the flat shape the plugin hooks consume.
 */
export const resolveOptions = (
  options: LitPluginOptions,
  env: Record<string, string>
): ResolvedOptions => {
  // The env var a Setting is read from is named once, on its definition.
  const envOf: EnvOf = (key) => env[SETTINGS[key].env!];
  const hmr = resolveHmr(options, env, envOf);
  const ind = resolveIndicator(hmr.hmrObj, hmr.enabled.value, envOf);

  const sources: SettingSources = {
    hmr: hmr.enabled.source,
    hmrReconnect: hmr.reconnect.source,
    hmrOnIncompatible: hmr.onIncompatible.source,
    hmrChildState: hmr.childState.source,
    hmrIndicatorVisible: ind.enabled.source,
    hmrIndicatorCount: ind.count.source,
  };

  const sourceOverlay = resolveSourceOverlay(
    options.sourceOverlay,
    envOf,
    sources
  );

  const timeline = pick(
    options.timeline,
    envBool(envOf('timeline')),
    SETTINGS.timeline.default
  );
  sources.timeline = timeline.source;

  const cssSheetBuild = resolveCssSheetBuild(options.cssSheetBuild, env);

  // A boolean-ish env value toggles; anything else names the folder.
  const envWorkspace = env[`${ENV_PREFIX}_DEVTOOLS_WORKSPACE`] || undefined;
  const devtoolsWorkspace =
    options.devtoolsWorkspace ?? envBool(envWorkspace) ?? envWorkspace ?? true;

  return {
    hmrEnabled: hmr.enabled.value,
    reconnect: hmr.reconnect.value,
    privateFields: hmr.privateFields.value,
    onIncompatible: hmr.onIncompatible.value,
    childState: hmr.childState.value,
    indicator: ind.value,
    sourceOverlay,
    timeline: timeline.value,
    cssSheetBuild,
    devtoolsWorkspace,
    sources,
  };
};

/**
 * Flattens resolved options into the JSON-serializable shape the panel's
 * Settings tab consumes (callback options like `exclude`/`onSelect` dropped;
 * a custom editor object reported as `"custom"`). Default key/editor/throttle
 * are applied here so the panel shows the effective values the runtime uses.
 */
export const toFeatureSettings = (r: ResolvedOptions): FeatureSettings => {
  const so = r.sourceOverlay;
  const editor = so === false ? undefined : so.editor;
  return {
    hmr: {
      enabled: r.hmrEnabled,
      reconnect: r.reconnect,
      onIncompatible: r.onIncompatible,
      childState: r.childState,
      indicatorEnabled: r.indicator !== false,
      indicatorCount: r.indicator !== false && r.indicator.count,
    },
    sourceOverlay: {
      enabled: so !== false,
      key:
        (so === false ? undefined : so.key) ??
        SETTINGS.sourceOverlayKey.default,
      editor:
        configuredEditor(r) ??
        (editor ? 'custom' : SETTINGS.sourceOverlayEditor.default),
      throttleMs:
        (so === false ? undefined : so.throttleMs) ??
        SETTINGS.sourceOverlayThrottleMs.default,
    },
    timeline: r.timeline,
    sources: {...r.sources},
  };
};

/**
 * The editor the developer named, if any. Unlike {@link toFeatureSettings},
 * which reports `vscode` for "never chose", this stays `undefined` then, so a
 * default never turns into a forced launcher.
 */
export const configuredEditor = (r: ResolvedOptions): string | undefined => {
  const editor = r.sourceOverlay === false ? undefined : r.sourceOverlay.editor;
  return typeof editor === 'string' ? editor : undefined;
};
